import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import type { SortState, SortableColumn } from '../types/claims.types';
import { isSortable, type Column } from '../utils/claim-columns';

interface ColumnHeaderProps {
  readonly column: Column;
  readonly sort: SortState | null;
  onToggleSort: (column: SortableColumn) => void;
}

/**
 * A sortable header. `aria-sort` carries the current direction, so the ordering is announced rather
 * than only drawn (WCAG 1.3.1).
 */
export function ColumnHeader({ column, sort, onToggleSort }: ColumnHeaderProps): React.JSX.Element {
  if (!isSortable(column)) {
    return (
      <th scope="col" className="text-muted px-3 py-2 font-semibold">
        {column.label}
      </th>
    );
  }

  const active = sort?.column === column.key;
  const ariaSort = active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none';

  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={`text-muted px-3 py-2 font-semibold ${column.align === 'right' ? 'text-right' : ''}`}
    >
      <button
        type="button"
        onClick={(): void => {
          onToggleSort(column.key);
        }}
        className="hover:text-brand-600 inline-flex items-center gap-1"
      >
        {column.label}
        {!active && <ChevronsUpDown aria-hidden="true" className="h-3 w-3 opacity-50" />}
        {active && sort.direction === 'asc' && <ChevronUp aria-hidden="true" className="h-3 w-3" />}
        {active && sort.direction === 'desc' && <ChevronDown aria-hidden="true" className="h-3 w-3" />}
      </button>
    </th>
  );
}
